import React, { useEffect, useMemo } from "react"
import { useGetTutorial } from "../../hooks/Tutorial/useGetTutorial"
import { tutorialMemory } from "../../Objects/Memory/Memory"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { AlertCircle, FolderOpen, PlayCircle, Youtube } from "lucide-react"
import { ETutorialAudienceType } from "../../Enum/ETutorialAudienceType"
import { extractYoutubeId } from "../../Objects/Generics/Helps"

export const CourierTutorialsPage: React.FC = () => {
  const { handleGetTutorial } = useGetTutorial()

  useEffect(() => {
    handleGetTutorial(() => {}, undefined, ETutorialAudienceType.Courier)
  }, [])

  const groupedTutorials = useMemo(() => {
    const grouped = new Map<string, typeof tutorialMemory.listData>()

    tutorialMemory.listData.forEach((item) => {
      const key = item.category.trim() || "Sem categoria"
      const current = grouped.get(key) ?? []

      current.push(item)

      grouped.set(key, current)
    })

    return Array.from(grouped.entries())
  }, [tutorialMemory.listData.length])

  if (groupedTutorials.length === 0) {
    return (
      <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
        <div className="p-1 md:p-2">
          <section className="rounded-2xl border-2 border-orange-100 bg-white p-5 text-center shadow-sm">
            <p className="text-xs font-black uppercase tracking-wide text-orange-600">Tutoriais para entregadores</p>

            <h2 className="mt-2 text-base font-bold text-slate-900">Nenhum tutorial disponível no momento</h2>

            <p className="mt-2 text-sm text-slate-500">
              Assim que novos vídeos forem cadastrados pelo administrador para entregadores, eles aparecerão aqui.
            </p>
          </section>
        </div>
      </Animate>
    )
  }

  return (
    <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
      <div className="w-full space-y-5 overflow-y-auto p-1 md:space-y-6 md:p-2">
        {groupedTutorials.map(([category, items], index) => (
          <Animate key={category} type={AnimationTypes.slideRightIn} duration={`${0.2 + index * 0.15}s`}>
            <section className="w-full overflow-hidden rounded-lg border border-orange-100 bg-white shadow-sm transition-all hover:shadow-md">
              <div className="flex items-center justify-between gap-3 border-b border-orange-50 bg-gradient-to-r from-orange-50 via-white to-white px-4 py-3 md:px-5 md:py-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-600">
                    <FolderOpen size={18} />
                  </div>

                  <div className="min-w-0">
                    <h2 className="truncate text-sm font-black uppercase tracking-wide text-orange-600 md:text-base">
                      {category}
                    </h2>

                    <p className="text-xs text-slate-500 md:text-sm">
                      {items.length} {items.length === 1 ? "tutorial" : "tutoriais"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-2 py-2 md:px-3 md:py-2">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {items.map((item) => {
                    const videoId = extractYoutubeId(item.youtubeUrl)

                    return (
                      <article
                        key={item.id}
                        className="group w-full min-w-0 rounded-lg border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                      >
                        <div className="p-3 md:p-4">
                          <div className="mb-3 flex items-start gap-3">
                            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-500">
                              <Youtube size={20} />
                            </div>

                            <div className="min-w-0 flex-1">
                              <h3 className="line-clamp-2 text-sm font-bold text-slate-900 md:text-base">
                                {item.title}
                              </h3>
                              <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                                <PlayCircle size={14} />
                                <span>Vídeo tutorial</span>
                              </div>
                            </div>
                          </div>

                          <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-100 shadow-inner">
                            <div className="aspect-video">
                              {videoId ? (
                                <iframe
                                  className="h-full w-full"
                                  src={`https://www.youtube.com/embed/${videoId}`}
                                  title={item.title}
                                  loading="lazy"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                  referrerPolicy="strict-origin-when-cross-origin"
                                  allowFullScreen
                                />
                              ) : (
                                <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-4 text-center text-slate-500">
                                  <AlertCircle size={18} className="text-orange-500" />
                                  <span className="text-xs font-medium md:text-sm">Link do YouTube inválido</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </article>
                    )
                  })}
                </div>
              </div>
            </section>
          </Animate>
        ))}
      </div>
    </Animate>
  )
}
